import React, { FC } from 'react';
import { Box } from '@mui/material';
import {
  SQUAD_PORTFOLIO_PIE_FONT_FAMILY,
  SQUAD_TABLE_BORDER_BOTTOM,
  SQUAD_TABLE_BORDER_TOP,
  SQUAD_TABLE_BORDER_WIDTH_PX,
  SQUAD_TABLE_COLUMN_GAP_PX,
  SQUAD_TABLE_FONT_SIZE_PX,
  SQUAD_TABLE_ROW_PAD_Y_PX,
  SQUAD_TABLE_TEXT_COLOR,
  squadTableLabelWhiteSpace,
} from './squadPortfolioChartUtils';

type Row = { label: string; value: string };

type Props = {
  rows: Row[];
  /** Optional header row (bold) — matches client when a pin/header row is set. */
  header?: Row;
  fontSize?: number;
};

/**
 * Factsheet portfolio table — visual match for client `custom-portfolio-table`
 * (themeIndex 2 borders, label takes the rest | value as wide as its text, right-aligned, body weight 400).
 */
export const SquadPortfolioTable: FC<Props> = ({
  rows,
  header,
  fontSize = SQUAD_TABLE_FONT_SIZE_PX,
}) => {
  const rowSx = {
    display: 'grid',
    gridTemplateColumns: 'minmax(0, 1fr) max-content',
    columnGap: `${SQUAD_TABLE_COLUMN_GAP_PX}px`,
    alignItems: 'start',
    width: '100%',
    minWidth: 0,
    py: `${SQUAD_TABLE_ROW_PAD_Y_PX}px`,
    px: 0,
    borderBottom: `${SQUAD_TABLE_BORDER_WIDTH_PX}px solid ${SQUAD_TABLE_BORDER_BOTTOM}`,
    boxSizing: 'border-box' as const,
  };

  const cellBase = {
    minWidth: 0,
    margin: 0,
    color: SQUAD_TABLE_TEXT_COLOR,
    fontSize,
    lineHeight: 1.25,
    textAlign: 'left' as const,
    fontWeight: 400,
  };

  const labelCellSx = (label: string, weight: number) => {
    const ws = squadTableLabelWhiteSpace(label);
    return {
      ...cellBase,
      fontWeight: weight,
      whiteSpace: ws,
      ...(ws === 'normal'
        ? { overflowWrap: 'anywhere' as const, wordBreak: 'break-word' as const }
        : null),
      ...(ws === 'pre-line' ? { whiteSpace: 'pre-line' as const } : null),
    };
  };

  const valueCellSx = (weight: number) => ({
    ...cellBase,
    fontWeight: weight,
    whiteSpace: 'nowrap' as const,
    textAlign: 'right' as const,
  });

  return (
    <Box
      className="squad-portfolio-table custom-portfolio-table"
      lang="de"
      sx={{
        width: '100%',
        minWidth: 0,
        fontFamily: SQUAD_PORTFOLIO_PIE_FONT_FAMILY,
        fontSize,
        color: SQUAD_TABLE_TEXT_COLOR,
      }}
    >
      {header ? (
        <Box
          sx={{
            ...rowSx,
            borderTop: `${SQUAD_TABLE_BORDER_WIDTH_PX}px solid ${SQUAD_TABLE_BORDER_TOP}`,
            marginBottom: '1px',
          }}
        >
          <Box sx={labelCellSx(header.label, 600)}>{header.label}</Box>
          <Box sx={valueCellSx(600)}>{header.value}</Box>
        </Box>
      ) : null}

      {(rows || []).map((row, index) => (
        <Box
          key={index}
          sx={{
            ...rowSx,
            ...(!header && index === 0
              ? { borderTop: `${SQUAD_TABLE_BORDER_WIDTH_PX}px solid ${SQUAD_TABLE_BORDER_TOP}` }
              : null),
          }}
        >
          <Box sx={labelCellSx(row.label, 400)}>{row.label}</Box>
          <Box sx={valueCellSx(400)}>{row.value}</Box>
        </Box>
      ))}
    </Box>
  );
};
